import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Calendar, Clock } from 'lucide-react';

const SEOHead = ({ title, description, slug }) => {
  useEffect(() => {
    document.title = title;
    const setMeta = (attr, key, content) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMeta('name', 'description', description);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:type', 'article');
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.setAttribute('rel', 'canonical'); document.head.appendChild(canonical); }
    canonical.setAttribute('href', `https://ascendforyou.net/blog/${slug}`);
  }, [title, description, slug]);
  return null;
};

const BLOG_POSTS = {
  'how-to-beat-ats-systems': {
    title: 'How to Beat ATS Systems in 2026: The Complete Guide',
    description: 'Learn exactly how applicant tracking systems work and the proven strategies to get your resume past ATS filters and into the hands of recruiters.',
    date: 'April 10, 2026',
    readTime: '8 min read',
    content: [
      {
        heading: 'What Is an ATS and Why Does It Matter?',
        text: `An Applicant Tracking System (ATS) is software that employers use to manage job applications. Over 90% of Fortune 500 companies and 75% of all employers use ATS to filter resumes before a human ever sees them. This means your resume needs to pass a robot before it reaches a recruiter.

The harsh reality: up to 75% of qualified candidates are rejected by ATS before their resume is ever read by a person. Understanding how these systems work is the first step to beating them.`,
      },
      {
        heading: 'How ATS Systems Parse Your Resume',
        text: `ATS software scans your resume for specific keywords, phrases, and formatting. It creates a digital profile from your resume and ranks it against the job description. Here's what it looks for:

- **Keywords**: Job titles, skills, certifications, tools, and technologies mentioned in the job posting
- **Section Headers**: Standard headers like "Experience," "Education," "Skills" — not creative ones like "My Journey"
- **Work History**: Company names, dates, job titles in a consistent format
- **Education**: Degrees, institutions, graduation dates
- **Contact Information**: Email, phone, location`,
      },
      {
        heading: '10 Proven Strategies to Beat ATS',
        text: `**1. Use a Simple, Clean Format**
Avoid tables, columns, text boxes, headers/footers, and graphics. ATS can't parse these elements and will either skip or scramble the content.

**2. Mirror Keywords from the Job Description**
If the posting says "project management," use that exact phrase — not "managed projects." ATS matching is often literal.

**3. Use Standard Section Headers**
Stick with: Summary, Experience, Education, Skills, Certifications. Avoid creative alternatives.

**4. Submit as .docx**
Unless specifically asked for PDF, .docx is the safest format. Some older ATS systems can't properly parse PDFs.

**5. Include a Skills Section**
Create a dedicated skills section with keywords from the job posting. This gives ATS an easy place to find matches.

**6. Spell Out Acronyms**
Write "Search Engine Optimization (SEO)" not just "SEO." Include both the full term and abbreviation.

**7. Use Standard Job Titles**
If your actual title was "Customer Happiness Ninja," translate it to "Customer Service Representative" for ATS matching.

**8. Quantify Your Achievements**
Include numbers: "Increased revenue by 35%" or "Managed team of 12 engineers." This signals relevance and impact.

**9. Tailor for Each Application**
Customize your resume for each job posting. One generic resume won't score well across different job descriptions.

**10. Check Your ATS Score Before Applying**
Use a free ATS checker to identify gaps before you submit. Fix issues while you can still make changes.`,
      },
      {
        heading: 'Common ATS Mistakes That Get Resumes Rejected',
        text: `- Using images or logos in your resume
- Putting important information in headers or footers
- Using unusual fonts that ATS can't read
- Submitting without tailoring keywords to the specific job
- Having an overly designed resume with multiple columns
- Missing standard sections that ATS expects to find
- Using abbreviations without spelling them out first`,
      },
    ],
  },
  'ats-resume-format-guide': {
    title: 'ATS-Friendly Resume Format: Templates & Examples for 2026',
    description: 'The definitive guide to formatting your resume for ATS compatibility. Includes free templates, examples, and formatting rules that work with every applicant tracking system.',
    date: 'April 10, 2026',
    readTime: '6 min read',
    content: [
      {
        heading: 'Why Resume Format Matters for ATS',
        text: `Your resume content might be perfect, but if the format is wrong, ATS will reject it. Applicant tracking systems read resumes top-to-bottom, left-to-right, and expect a specific structure. A beautifully designed resume that a human would love might be completely unreadable to ATS software.

The goal is creating a resume that looks professional to humans AND is perfectly parseable by robots.`,
      },
      {
        heading: 'The Perfect ATS Resume Format',
        text: `**Use Single-Column Layout**
Multi-column layouts confuse ATS parsers. Stick to a single column that flows naturally from top to bottom.

**Standard Fonts Only**
Use Arial, Calibri, Cambria, Georgia, Helvetica, or Times New Roman. Size 10-12pt for body text, 14-16pt for your name.

**Section Order That Works**
1. Contact Information (name, email, phone, LinkedIn, location)
2. Professional Summary (2-3 sentences with target keywords)
3. Work Experience (reverse chronological)
4. Education
5. Skills (keyword-rich list)
6. Certifications (if applicable)

**Date Formatting**
Use consistent date formats: "Jan 2023 - Present" or "01/2023 - Present"

**Bullet Points**
Use standard bullet characters. Avoid custom symbols, checkmarks, or arrows.`,
      },
      {
        heading: 'What to Avoid in ATS Resume Formatting',
        text: `- **Tables and text boxes**: ATS reads these incorrectly or skips them entirely
- **Headers and footers**: Many ATS systems ignore content in document headers/footers — don't put your contact info there
- **Images, logos, and graphics**: Completely invisible to ATS
- **Fancy dividers and lines**: Can cause parsing errors
- **Multiple columns**: Scrambles the reading order
- **Infographics or charts**: ATS can't interpret visual data
- **PDF with embedded images**: Some PDFs are image-based rather than text-based, making them unreadable`,
      },
      {
        heading: 'ATS Resume Format by Industry',
        text: `**Software Engineering**: Focus on technical skills section, include specific technologies, frameworks, and tools. List GitHub/portfolio links in contact info.

**Marketing**: Emphasize metrics and results. Include both strategic (brand management, market research) and tactical skills (Google Analytics, HubSpot, SEO).

**Healthcare**: Include licenses, certifications with numbers, and specialized training. Use exact medical terminology from job postings.

**Finance**: List specific tools (Bloomberg, SAP, Excel), certifications (CFA, CPA), and regulatory knowledge.

**Sales**: Quantify everything — revenue generated, quota attainment, client acquisition numbers. Include CRM experience.`,
      },
    ],
  },
  'why-resume-rejected-ats': {
    title: 'Why Your Resume Gets Rejected: 7 ATS Mistakes Costing You Interviews',
    description: 'Discover the 7 most common reasons ATS rejects qualified candidates and learn exactly how to fix each one. Stop getting ghosted by employers.',
    date: 'April 10, 2026',
    readTime: '5 min read',
    content: [
      {
        heading: 'You\'re Qualified — So Why No Callbacks?',
        text: `You've applied to dozens of jobs. You have the right experience, the right skills, and the right education. But your phone isn't ringing and your inbox is empty. Sound familiar?

The problem likely isn't you — it's your resume format and keyword strategy. Applicant Tracking Systems reject up to 75% of resumes before a recruiter ever sees them. Here are the 7 most common reasons why.`,
      },
      {
        heading: 'Mistake #1: Using a Creative Resume Template',
        text: `Those beautiful two-column templates from Canva or Etsy? ATS can't read them. Multi-column layouts, text boxes, and graphic elements cause the parser to scramble your information or skip it entirely.

**Fix**: Use a simple, single-column format. It doesn't have to be ugly — clean and minimal works for both ATS and humans.`,
      },
      {
        heading: 'Mistake #2: Missing Keywords from the Job Description',
        text: `ATS performs keyword matching between your resume and the job posting. If the job says "project management" and your resume says "managed projects," some systems won't match that.

**Fix**: Read the job description carefully and mirror the exact language. If they say "data analysis," use "data analysis" — not "analyzing data" or "data analytics."`,
      },
      {
        heading: 'Mistake #3: Submitting the Wrong File Format',
        text: `Some ATS systems struggle with PDFs, especially those created from design tools that embed text as images. Others can't handle .pages or .odt files.

**Fix**: Submit as .docx unless the posting specifically requests PDF. When in doubt, .docx is the safest choice.`,
      },
      {
        heading: 'Mistake #4: Contact Info in the Header/Footer',
        text: `Many ATS systems completely ignore document headers and footers. If your name, email, and phone number are in the header, the system might not capture them at all.

**Fix**: Put all contact information in the main body of the document, at the very top.`,
      },
      {
        heading: 'Mistake #5: Using One Resume for Every Application',
        text: `A generic resume will score poorly against specific job descriptions. Each posting has unique keywords and requirements.

**Fix**: Customize your resume for each application. At minimum, update your summary and skills section to match the job posting's language.`,
      },
      {
        heading: 'Mistake #6: Leaving Out a Skills Section',
        text: `Without a dedicated skills section, ATS has to hunt through your experience to find keyword matches. This reduces your score.

**Fix**: Add a clear "Skills" section listing relevant hard and soft skills. Include tools, technologies, methodologies, and certifications.`,
      },
      {
        heading: 'Mistake #7: Not Checking Your ATS Score Before Submitting',
        text: `Most people submit their resume and hope for the best. But you can check your ATS compatibility before you apply.

**Fix**: Use a free ATS score checker to identify gaps and fix them before submitting. It takes 30 seconds and can mean the difference between getting an interview and getting filtered out.`,
      },
    ],
  },
  'workday-ats-guide': {
    title: 'Workday ATS Decoded: What It Parses & How to Optimize Your Resume',
    description: "Workday is the ATS used by 60%+ of Fortune 500 companies. Here's exactly what it extracts from your resume, where it trips up, and the formatting changes that move your application from 'screened out' to 'interview pile.'",
    date: 'May 14, 2026',
    readTime: '9 min read',
    content: [
      {
        heading: 'Why Workday Matters More Than You Think',
        text: `If you've applied to jobs at Salesforce, Netflix, Bank of America, Adobe, Walmart, Target, or Amazon recently, your resume has been read by Workday — not a human, at least not first. Workday Recruiting is the dominant ATS in enterprise hiring, used by more than 60% of Fortune 500 companies and over 10,000 employers worldwide.

That makes Workday the single most important ATS to optimize for. The good news: once you know its quirks, you can engineer a resume that consistently parses cleanly and ranks higher. The bad news: it has more quirks than most candidates realize.`,
      },
      {
        heading: 'What Workday Actually Extracts From Your Resume',
        text: `When you upload a resume, Workday runs it through an internal parser that pulls out structured fields. The system isn't reading your resume the way a recruiter does — it's filling out a form:

- **Contact info**: name, email, phone, location (city + state)
- **Work history**: each role as { company, title, start date, end date, description }
- **Education**: each degree as { school, degree, field of study, graduation date }
- **Skills**: a flat list extracted from anywhere in the document
- **Certifications & licenses**: pulled if formatted clearly
- **Languages spoken**: only if explicitly labeled

Anything Workday can't fit into one of those buckets gets dropped or buried. That's why a beautifully designed two-column resume with a "Career Highlights" sidebar can score lower than an ugly one-column resume with standard sections — the ugly one parses cleanly.`,
      },
      {
        heading: 'The 7 Workday Parsing Traps (and the Fix for Each)',
        text: `**1. Two-column layouts**
Workday reads left-to-right, top-to-bottom across the full page width. A two-column layout often gets read as one long mangled column, mixing your skills section into the middle of your job descriptions.
*Fix*: Use a single-column layout for any role you really want.

**2. Dates in non-standard formats**
"Spring 2024" or "January '24" can confuse the parser. Workday expects MM/YYYY or Month YYYY.
*Fix*: Write dates as "Jan 2024 – Present" or "01/2024 – Present".

**3. Job titles inside the company name field**
If you write "Acme Corporation – Senior Engineer," Workday sometimes parses "Acme Corporation – Senior Engineer" as the company name and leaves the title field blank.
*Fix*: Put title and company on separate lines, with the title clearly labeled.

**4. Skills buried in paragraphs**
Workday's skill extractor scans the whole document, but it weights skills found in a dedicated "Skills" section much higher.
*Fix*: Have an explicit "Skills" or "Technical Skills" section listing 10-25 keywords from the job description.

**5. Graphics, icons, and logos**
PNG icons next to section headers, charts showing "proficiency levels," and company logos all get stripped — and sometimes corrupt the surrounding text.
*Fix*: Remove all imagery from any resume you submit through Workday.

**6. Headers and footers**
Contact info placed in the Word header/footer (a common space-saving trick) is invisible to Workday.
*Fix*: Put your name, email, phone, and location in the body of the document, at the top.

**7. PDFs exported from design tools**
Resumes built in Canva, Adobe Illustrator, or InDesign often export PDFs as a series of image fragments. Workday treats those as picture files, not text.
*Fix*: Use Microsoft Word or Google Docs to build the version you submit to Workday. Save designed versions for direct-to-recruiter outreach.`,
      },
      {
        heading: 'The Workday Keyword Match Score',
        text: `Workday uses a proprietary scoring algorithm to rank applicants against the job description. The exact weights are private, but recruiters who manage Workday have shared the broad strokes:

- **Exact phrase matches** count more than synonym matches. "Project Management Professional" scores higher than "PMP" if the job says "Project Management Professional."
- **Recent experience** counts more than older. Skills used in your current role weigh more than skills used 8 years ago.
- **Section placement matters**. A skill in your Skills section + Experience section + Summary scores triple compared to a skill mentioned only once.
- **Density matters but isn't everything**. Stuffing the same keyword 15 times can trigger anti-keyword-stuffing rules. Aim for natural mentions in 2-3 places.`,
      },
      {
        heading: 'How to Test Your Resume Against a Workday Job Posting',
        text: `Before you submit, do this 10-minute check:

1. Open the job description in one window, your resume in another
2. Highlight every hard skill, tool, certification, and required qualification in the JD
3. For each highlighted item, search your resume — does it appear?
4. For the ones missing, ask yourself: do I actually have this skill, just under a different name? If yes, rephrase to match.
5. Run your resume through a free ATS checker that simulates Workday's parsing behavior

The free checker at [ascendforyou.net/free-ats-check](https://ascendforyou.net/free-ats-check) does steps 3-5 in 30 seconds and tells you which keywords are missing for the specific role you're targeting.`,
      },
      {
        heading: 'One Last Thing: The "Quick Apply" Trap',
        text: `Workday's "Quick Apply" feature pulls fields from your LinkedIn profile or a previously uploaded resume. It's fast — but it bypasses your carefully tuned resume in favor of whatever Workday last parsed.

If you're applying to a role you really care about, ALWAYS upload a fresh, role-tailored resume even when Quick Apply is offered. The 2 extra minutes it takes can be the difference between a 60% match score and a 90% match score — and Workday is unforgiving about the gap.`,
      },
    ],
  },
  'chatgpt-resume-prompts': {
    title: 'ChatGPT for Resumes: 12 Prompts That Actually Work (Tested 2026)',
    description: 'Most ChatGPT resume advice produces generic, easily-detected content that hurts more than helps. These 12 prompts — tested against real ATS systems and recruiter feedback — produce keyword-rich, human-sounding resume bullets that pass both the bot and the human.',
    date: 'May 14, 2026',
    readTime: '7 min read',
    content: [
      {
        heading: 'Why Most ChatGPT Resume Prompts Fail',
        text: `Walk into any LinkedIn comment section in 2026 and you'll find someone telling you to paste your resume into ChatGPT and ask it to "make it better." The output sounds polished. It also sounds like every other AI-generated resume that recruiter has read this week — and modern ATS systems (especially Greenhouse and Lever) now flag overly-templated language as a negative signal.

The problem isn't AI. The problem is the prompts. Generic prompts produce generic output. Targeted prompts that give ChatGPT real context, examples, and constraints produce bullets that sound like a senior writer wrote them. Below are 12 prompts we've tested against actual ATS scoring and recruiter A/B feedback.`,
      },
      {
        heading: 'Prompts 1-3: Rewriting a Weak Bullet',
        text: `**Prompt 1 — Add quantification to a vague bullet**
> Here is a resume bullet from my [JOB TITLE] role: "[PASTE BULLET]". Rewrite it three different ways. Each version must include a specific measurable result (percent, dollar amount, time saved, or count). If the original bullet doesn't have a number, propose realistic ranges for me to verify. Keep each version under 25 words. Lead with a strong action verb (no "responsible for" or "helped with").

**Prompt 2 — Reframe for a target role**
> I'm currently a [CURRENT ROLE] applying for [TARGET ROLE]. Here's a bullet describing what I do today: "[PASTE BULLET]". Rewrite this bullet to emphasize the skills and outcomes that matter most for the target role, while staying truthful to what I actually did. Use vocabulary common in the target role's job postings. Return three options.

**Prompt 3 — Match a specific job description**
> Job description: [PASTE JD]. My current bullet: "[PASTE BULLET]". Rewrite the bullet so it ranks higher in an ATS scan against this specific JD. Mirror the JD's exact keywords where I genuinely have that experience. Do not invent skills I don't claim. Return three options ranked by ATS impact.`,
      },
      {
        heading: 'Prompts 4-6: Building From Scratch',
        text: `**Prompt 4 — Generate bullets from a project description**
> I led a project at [COMPANY] from [START] to [END]. Here's what I did: [PARAGRAPH IN YOUR OWN WORDS]. Convert this into 4-5 resume bullets following these rules:
> - Each bullet starts with a strong past-tense action verb (no "responsible for")
> - Each bullet includes a specific outcome or metric
> - No bullet exceeds 2 lines / 25 words
> - Use vocabulary that would resonate with a [TARGET INDUSTRY] hiring manager

**Prompt 5 — Write a professional summary**
> Write a 3-sentence resume summary for someone with the following background: [PASTE 2-3 SENTENCES]. The target role is [TITLE]. The summary must:
> - Open with years of experience and core specialty
> - Mention 2-3 specific tools or methodologies from the target field
> - Close with a concrete outcome statement (what kind of value they deliver)
> Do not use the words "passionate," "driven," "results-oriented," or "team player."

**Prompt 6 — Create a skills section from a JD**
> Here's a job description: [PASTE JD]. Here's my resume: [PASTE RESUME]. Generate a Skills section with three subsections: Technical Skills, Tools & Platforms, Methodologies. Only include skills I genuinely have based on what's in my resume, but use the exact phrasing from the JD wherever there's a match. Order each subsection from most-emphasized in the JD to least.`,
      },
      {
        heading: 'Prompts 7-9: Polishing & Final Pass',
        text: `**Prompt 7 — Catch passive voice and weak verbs**
> Here's my resume: [PASTE]. Identify every bullet that uses passive voice, weak verbs (was, were, helped, assisted, supported, participated in), or filler phrases. For each one, suggest a stronger replacement. Return as a table: original | issue | replacement.

**Prompt 8 — Add industry buzzwords without overstuffing**
> Job posting: [PASTE JD]. My resume: [PASTE]. Identify 5-8 keywords from the JD that are NOT in my resume but that I could honestly claim based on the experience I describe. For each, suggest exactly where to add it (which bullet, which section) in a natural way. Don't suggest stuffing the same keyword more than 3 times.

**Prompt 9 — Cut the resume to one page**
> Here's a 2-page resume: [PASTE]. Cut it down to one page while preserving the most impactful content. Apply these rules:
> - Keep all bullets with quantified results
> - Remove or shorten bullets older than 8 years
> - Compress education down to 1-2 lines if 10+ years of experience
> - Combine repetitive bullets across roles
> Show me the trimmed version with deletions marked.`,
      },
      {
        heading: 'Prompts 10-12: The Cover Letter & Outreach',
        text: `**Prompt 10 — Generate a cover letter draft**
> Write a 3-paragraph cover letter for the following role: [PASTE JD]. My resume highlights: [PASTE 3-5 BULLETS]. The letter must:
> - Open with a hook tied to something specific about the company (not "I am writing to apply for")
> - Middle paragraph: connect 2 of my accomplishments to 2 specific requirements in the JD
> - Close with a forward-looking sentence and a specific ask (e.g., "I'd welcome a 15-minute call to discuss…")
> Keep it under 200 words. Sound like a thoughtful adult, not a corporate template.

**Prompt 11 — LinkedIn outreach to a hiring manager**
> I'm applying to [ROLE] at [COMPANY] and want to message the hiring manager on LinkedIn. Here's their LinkedIn bio: [PASTE]. My background: [PASTE 2 SENTENCES]. Write a 4-5 sentence LinkedIn message that:
> - References something specific from their bio
> - Mentions one accomplishment of mine that's relevant
> - Asks for a 15-minute conversation
> - Doesn't sound like a sales pitch

**Prompt 12 — Interview prep using your resume**
> Here's my resume: [PASTE]. Here's the JD: [PASTE]. Generate 8 likely interview questions specific to this role + my background. For each question, suggest a STAR-method answer outline (Situation, Task, Action, Result) using my actual experience. Identify the 2 questions I should rehearse most carefully because they target potential weak spots in my resume.`,
      },
      {
        heading: 'The Meta-Trick That Multiplies All Of These',
        text: `Whatever prompt you use, end it with: *"Before giving your final answer, ask me up to 3 clarifying questions if any context would meaningfully improve the output."*

ChatGPT will often respond with questions like "What metric mattered most for this project?" or "Was this work individual or team-based?" — questions that, when answered, produce dramatically more specific and authentic-sounding output than the first-pass response.

The reason most AI-generated resumes sound generic is the human didn't give the AI enough context. This single sentence forces the AI to ask for what it needs.`,
      },
      {
        heading: 'Where AI Stops and You Start',
        text: `ChatGPT can rewrite, structure, and quantify. It can't tell you which projects to highlight, which 8 years of your career matter most for the next role, or whether your resume actually feels true to who you are. That's still your job.

The best workflow we see: use AI to do the heavy lifting on language and structure, then spend 15 minutes reading the final version aloud. If a bullet sounds like a stranger wrote it, edit it back into your own voice — even if that means breaking a grammar rule or two. Recruiters can smell sterile AI output from a mile away. Authenticity always wins the tie.

If you want a head start, paste your current resume into the free ATS checker at [ascendforyou.net/free-ats-check](https://ascendforyou.net/free-ats-check). It scores your resume, identifies the weakest bullets, and suggests rewrites — applying every prompt above automatically.`,
      },
    ],
  },
  'free-ats-resume-checker': {
    title: 'How to Use a Free ATS Resume Checker to Stop Being Ghosted (2026 Guide)',
    description: "Stop losing $1,235/week. Learn how to use Ascend's free ATS checker to find keyword gaps and beat the bots in 30 seconds.",
    date: 'May 17, 2026',
    readTime: '7 min read',
    content: [
      {
        heading: 'The Ghosting Isn\'t Personal. It\'s Algorithmic.',
        text: `If you've applied to 30+ jobs and heard back from fewer than three, you're not failing — you're being filtered. 90% of large employers run every resume through an Applicant Tracking System (ATS) before a human ever sees it. The ATS scores you in milliseconds. If your score is below the cutoff, you're auto-rejected. No email, no rejection notice — just silence.

That silence has a price tag. The average mid-career professional loses roughly **$1,235 per week** of unemployment in foregone wages. Most of that loss is sitting in a "Resume submitted — under review" status that the ATS already dismissed.

The fix isn't hustle. It's a 30-second diagnostic with a free ATS resume checker. This guide walks through exactly how to use one — what to look for, what to fix first, and how to stop being ghosted on roles you're actually qualified for.`,
      },
      {
        heading: 'What an ATS Resume Checker Actually Does',
        text: `An ATS resume checker simulates the same parsing logic used by hiring platforms like Workday, Greenhouse, Taleo, iCIMS, and Lever. The good ones do four things:

- **Parse your resume the way an ATS would** — extracting work history, skills, education, and contact info from your formatting
- **Score your keyword overlap** against a target job description, returning a number from 0–100
- **Surface the missing keywords** — the ones the JD calls for that don't appear on your resume
- **Flag formatting traps** — tables, columns, headers/footers, and graphics that ATS systems mis-parse

The bad ones do one thing: give you a vague "Your resume looks good!" without showing the math. **Avoid those.** A real ATS checker tells you exactly which keywords are missing and where to add them.`,
      },
      {
        heading: 'The 30-Second Workflow',
        text: `Here's the exact process. It takes less than a minute per job.

**Step 1 — Open the job description.** Pick a real posting you'd apply to today. Copy the entire description, not just the job title.

**Step 2 — Paste your resume.** Open Ascend's free ATS checker at [ascendforyou.net/free-ats-check](https://ascendforyou.net/free-ats-check). Paste your resume into the left panel. No signup, no credit card.

**Step 3 — Paste the JD.** Drop the job description into the right panel. Click "Score My Resume."

**Step 4 — Read the gap report.** You'll get three things:
- An overall ATS score (0–100)
- A list of keywords matched
- A list of keywords missing

**Step 5 — Fix the missing keywords.** Open your resume in a separate window. For each missing keyword, ask: *do I actually have this skill?* If yes, rephrase a bullet to use the exact wording from the JD. If no, leave it out — never invent skills.

**Step 6 — Re-score.** Paste the updated resume back in. Aim for 80+. Below 65, you're likely getting filtered; above 80, you're competitive.

That's it. The whole loop is 30 seconds to score, 5 minutes to fix. For most resumes, this single optimization adds 20–30 points to the ATS score.`,
      },
      {
        heading: 'Why Free Beats Paid for First-Pass Checking',
        text: `There are paid ATS checkers in the market that charge $40–$100 for a single resume scan. Most of them wrap the same parsing logic in a fancier UI. For a first-pass diagnostic — the kind of "is my resume in the ballpark?" check you need before every application — a free tool is enough.

Ascend's free ATS checker is genuinely free:
- No credit card to score your resume
- No "free trial" countdown
- No upsell wall on the score itself
- Multiple checks per day (rate-limited per IP for abuse prevention)

The free score includes the full keyword match/miss breakdown. The AI rewrite preview shows what Sage would change — sign up to apply rewrites, generate cover letters, and unlock the full toolkit.`,
      },
      {
        heading: 'The 4 Things to Fix First',
        text: `When your ATS score comes back below 65, here's the prioritized fix list. Work top-down.

**1. Missing hard-skill keywords (biggest score gain).**
If the JD says "SQL" five times and your resume says "database querying" once, you'll under-score by 15+ points. Mirror the JD's exact wording for tools, languages, certifications, and methodologies — assuming you genuinely have the skill.

**2. Wrong section headers (silent killer).**
ATS systems look for "Experience," "Education," "Skills" — not "My Career Journey" or "What I've Built." If your headers are creative, the ATS may not extract your work history at all, dropping your score to near-zero on parseable content.

**3. Tables, columns, and text boxes (the parsing trap).**
Anything formatted in a multi-column layout, table, or text box gets mis-parsed by older ATS engines (especially Taleo and older Workday configurations). Single-column, top-to-bottom resumes parse cleanly.

**4. Acronyms without the spell-out.**
"SEO" alone scores lower than "Search Engine Optimization (SEO)." Include both the full term and the abbreviation the first time. The ATS indexes both spellings; reviewers scan for the one they're looking for.`,
      },
      {
        heading: 'When to Re-Check (and When to Stop)',
        text: `Re-check your resume every time the target role changes substantially. A Senior Product Manager JD at Stripe will rank keywords completely differently than a PM JD at a healthcare startup. **One resume per company tier minimum, ideally one per JD.**

Stop tweaking when you hit 80+ on three different JDs in the same role. At that point, the residual variance is noise. Move your energy from optimization to outreach: warm intros, recruiter follow-ups, and tailored cover letters move the needle more than chasing an 85 → 88 ATS bump.

The compounding return on a 30-second ATS check is enormous. If a 5-minute fix turns one ghosted application per week into one phone screen, you've reclaimed roughly $1,235 of weekly opportunity cost. The math gets uncomfortable fast for anyone NOT doing the check.`,
      },
      {
        heading: 'Try It Right Now',
        text: `Score your resume free at [ascendforyou.net/free-ats-check](https://ascendforyou.net/free-ats-check). No signup. 30 seconds. You'll know within a minute whether you're being filtered out — and exactly what to fix.

If you score 80+, you're already in the top quartile. If you score 65–80, you're competitive but losing close races. If you score below 65, you're being filtered out before a human reads your resume — and that's a fixable problem.

Stop being ghosted. Start being scored honestly.`,
      },
    ],
  },
};

const BlogIndex = () => (
  <div className="min-h-screen bg-white" data-testid="blog-page">
    <section className="pt-24 pb-12 border-b border-[#e5e7eb]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
        <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#002fa7] mb-3 block">RESOURCES</span>
        <h1 className="font-satoshi text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter text-[#0f1115] leading-[1.1]">
          ATS Resume Optimization Guide
        </h1>
        <p className="mt-4 text-base text-[#4b5563] max-w-xl mx-auto">
          Expert advice on beating applicant tracking systems and landing more interviews.
        </p>
      </div>
    </section>

    <section className="py-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 space-y-0 border border-[#e5e7eb]">
        {Object.entries(BLOG_POSTS).map(([slug, post]) => (
          <Link
            key={slug}
            to={`/blog/${slug}`}
            className="block p-8 border-b border-[#e5e7eb] last:border-b-0 hover:bg-[#f4f5f7] transition-colors group"
            data-testid={`blog-card-${slug}`}
          >
            <div className="flex items-center gap-4 text-xs text-[#6b7280] mb-3">
              <span className="flex items-center gap-1"><Calendar size={12} /> {post.date}</span>
              <span className="flex items-center gap-1"><Clock size={12} /> {post.readTime}</span>
            </div>
            <h2 className="font-satoshi text-xl font-bold text-[#0f1115] group-hover:text-[#002fa7] transition-colors mb-2">
              {post.title}
            </h2>
            <p className="text-sm text-[#4b5563] leading-relaxed">{post.description}</p>
            <span className="inline-flex items-center gap-1 text-sm font-semibold text-[#002fa7] mt-3">
              Read article <ArrowRight size={14} />
            </span>
          </Link>
        ))}
      </div>
    </section>

    <section className="py-12 bg-[#f4f5f7]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
        <h2 className="font-satoshi text-2xl font-bold text-[#0f1115] mb-4">
          Check your ATS score right now
        </h2>
        <p className="text-[#4b5563] mb-6">Free, instant, no signup required.</p>
        <Link to="/free-ats-check" className="btn-primary inline-flex items-center gap-2" data-testid="blog-cta">
          <span>Free ATS Score Check</span>
          <ArrowRight size={18} />
        </Link>
      </div>
    </section>
  </div>
);

const BlogPost = () => {
  const { slug } = useParams();
  const post = BLOG_POSTS[slug];

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-satoshi text-2xl font-bold mb-4">Article not found</h1>
          <Link to="/blog" className="text-[#002fa7] font-semibold hover:underline">Back to blog</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="blog-post-page">
      <SEOHead title={`${post.title} | Ascend`} description={post.description} slug={slug} />

      <article className="pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-[#6b7280] hover:text-[#002fa7] mb-8 transition-colors" data-testid="back-to-blog">
            <ArrowLeft size={14} /> All articles
          </Link>

          <div className="flex items-center gap-4 text-xs text-[#6b7280] mb-4">
            <span className="flex items-center gap-1"><Calendar size={12} /> {post.date}</span>
            <span className="flex items-center gap-1"><Clock size={12} /> {post.readTime}</span>
          </div>

          <h1 className="font-satoshi text-3xl sm:text-4xl font-black tracking-tighter text-[#0f1115] leading-tight mb-6">
            {post.title}
          </h1>
          <p className="text-lg text-[#4b5563] leading-relaxed mb-10 border-b border-[#e5e7eb] pb-10">
            {post.description}
          </p>

          <div className="space-y-10">
            {post.content.map((section) => (
              <div key={section.heading}>
                <h2 className="font-satoshi text-xl font-bold text-[#0f1115] mb-4">{section.heading}</h2>
                <div className="text-[#4b5563] leading-relaxed whitespace-pre-line text-sm">
                  {section.text.split('**').map((part, j) =>
                    j % 2 === 1 ? <strong key={part} className="text-[#0f1115]">{part}</strong> : part
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-12 bg-[#0f1115] p-8 text-center">
            <h3 className="font-satoshi text-xl font-bold text-white mb-2">
              Ready to check your ATS score?
            </h3>
            <p className="text-[#9ca3af] text-sm mb-6">
              Free, instant results. No signup required.
            </p>
            <Link to="/free-ats-check" className="btn-primary inline-flex items-center gap-2" data-testid="post-cta">
              <span>Check My ATS Score</span>
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </article>
    </div>
  );
};

export { BlogIndex, BlogPost, BLOG_POSTS };
